import * as React from 'react';

import { translate } from '~l10n/client';

import { PaywallScreens } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { DialogProps } from '~shared/components/Dialog';
import {
  PaywallDialog,
  PaywallTouch,
  PaywallUpgradeButton,
} from '~teacher/components/PaywallDialog';
import { useQuizLimit } from '~teacher/hook';

export type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  paywallTouch: PaywallTouch;
};

export function QuizzesPaywall({ open, onClose, paywallTouch }: Props) {
  if (paywallTouch[0] === PaywallScreens.QuizListMergeQuiz) {
    return <QuizzesMergeQuizPaywall {...{ open, onClose, paywallTouch }} />;
  }

  if (paywallTouch[0] === PaywallScreens.QuizListLimitReached) {
    return <QuizzesQuizLimitReached {...{ open, onClose, paywallTouch }} />;
  }

  if (paywallTouch[0] === PaywallScreens.QuizListAccessLocked) {
    return <QuizzesQuizAccessLocked {...{ open, onClose, paywallTouch }} />;
  }

  return null;
}

export function QuizzesQuizLimitReached({
  open,
  onClose,
  paywallTouch,
}: Props) {
  const quizLimit = useQuizLimit();

  return (
    <PaywallDialog.Short
      actions={[
        <UIButton key="cancel" onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <PaywallUpgradeButton
          key="upgrade"
          appearance="primary"
          paywallScreen={paywallTouch[0]}
        />,
      ]}
      paywallTouch={paywallTouch}
      open={open}
      onClose={onClose}
      id={'quiz-limit-modal'}
      title={translate('You’ve Reached Your {0} of {1} Quiz Limit').format(
        quizLimit.data?.limit ?? 0,
        quizLimit.data?.limit ?? 0
      )}
    >
      {translate('Upgrade to unlock unlimited quizzes.')}
    </PaywallDialog.Short>
  );
}

export function QuizzesMergeQuizPaywall({
  open,
  onClose,
  paywallTouch,
}: Props) {
  const title = translate('Upgrade to Merge Quizzes');
  const message = translate('Merge is only available on Socrative Pro.');

  return (
    <PaywallDialog.Short
      closeLabel={translate('Close')}
      id="quiz-list-paywall"
      key="quiz-list-paywall"
      title={title}
      actions={[
        <UIButton key="cancel" onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <PaywallUpgradeButton
          key="upgrade"
          appearance="primary"
          paywallScreen={paywallTouch[0]}
        />,
      ]}
      open={open}
      onClose={() => onClose(false)}
      paywallTouch={[paywallTouch[0]]}
    >
      <p className="m-0 pb-4 text-blue-900">{message}</p>
    </PaywallDialog.Short>
  );
}

export function QuizzesQuizAccessLocked({
  open,
  onClose,
  paywallTouch,
}: Props) {
  return (
    <PaywallDialog.Short
      actions={[
        <UIButton key="cancel" onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <PaywallUpgradeButton
          key="upgrade"
          appearance="primary"
          paywallScreen={paywallTouch[0]}
        />,
      ]}
      paywallTouch={paywallTouch}
      open={open}
      onClose={onClose}
      id={'quiz-limit-modal'}
      title={translate('Upgrade to Access Quiz')}
    >
      {translate(
        'Upgrade to unlock unlimited quizzes and access quizzes past your limit.'
      )}
    </PaywallDialog.Short>
  );
}
